import { useState, useEffect, useMemo, useRef } from 'react'
import { getApiBase } from '../../utils/api'
import type { Product } from '../../types'
import {
  ProductCategoryLabels,
  type PublicPassportHistory,
  type LifecycleEvent,
  type PublicMaintenanceRecord,
  type DppVersionEntry,
} from '@esf/shared'
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
} from 'chart.js'
import { Doughnut, Bar } from 'react-chartjs-2'
import QRCodeGenerator from './QRCodeGenerator'

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement)

interface PublicPassportPageProps {
  passportId: string
}

// Format date for display (British English)
const formatDate = (dateStr?: string) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

// Format short date for timeline
const formatShortDate = (dateStr?: string) => {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

// Score color based on value
const getScoreColor = (score: number, max: number) => {
  const percentage = (score / max) * 100
  if (percentage >= 70) return '#10B981' // Green
  if (percentage >= 40) return '#F59E0B' // Amber
  return '#EF4444' // Red
}

// Lifecycle event icon mapping
const lifecycleIcons: Record<string, string> = {
  MANUFACTURE: 'M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z',
  SHIPMENT: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  INSTALLATION: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  INSPECTION: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  MAINTENANCE: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
  REPAIR: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
  WARRANTY_START: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  WARRANTY_END: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  DECOMMISSION: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  EOL: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
}

// Lifecycle event color mapping
const lifecycleColors: Record<string, { bg: string; text: string; border: string }> = {
  MANUFACTURE: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  SHIPMENT: { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  INSTALLATION: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  INSPECTION: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  MAINTENANCE: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  REPAIR: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  WARRANTY_START: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  WARRANTY_END: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200' },
  DECOMMISSION: { bg: 'bg-gray-50', text: 'text-gray-700', border: 'border-gray-200' },
  EOL: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
}

// Score Gauge Component
function ScoreGauge({
  score,
  maxScore,
  label,
  unit,
  size = 120,
}: {
  score: number
  maxScore: number
  label: string
  unit?: string
  size?: number
}) {
  const percentage = Math.min((score / maxScore) * 100, 100)
  const color = getScoreColor(score, maxScore)

  const data = {
    datasets: [
      {
        data: [percentage, 100 - percentage],
        backgroundColor: [color, '#E5E7EB'],
        borderWidth: 0,
        cutout: '75%',
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
  }

  return (
    <div className="flex flex-col items-center animate-fade-in-up">
      <div className="relative" style={{ width: size, height: size }}>
        <Doughnut data={data} options={options} />
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Sora, sans-serif' }}>
            {score}
          </span>
          {unit && <span className="text-xs text-gray-500">{unit}</span>}
        </div>
      </div>
      <span className="mt-2 text-sm font-medium text-gray-600 text-center">{label}</span>
    </div>
  )
}

// Material Composition Chart
function MaterialCompositionChart({ materials }: { materials: any[] }) {
  if (!materials || materials.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No material composition data available
      </div>
    )
  }

  const colors = [
    '#013C7A', // Navy
    '#1A5DA8', // Light navy
    '#3B82F6', // Blue
    '#60A5FA', // Sky
    '#93C5FD', // Light sky
    '#E6204D', // Coral
    '#F59E0B', // Amber
    '#10B981', // Emerald
  ]

  const data = {
    labels: materials.map((m) => m.name || m.materialType || 'Unknown'),
    datasets: [
      {
        data: materials.map((m) => m.percentageByWeight || m.weightGrams || 0),
        backgroundColor: colors.slice(0, materials.length),
        borderWidth: 2,
        borderColor: '#FFFFFF',
      },
    ],
  }

  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          padding: 16,
          usePointStyle: true,
          pointStyle: 'circle',
          font: { family: 'Source Sans 3, sans-serif', size: 12 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed
            return ` ${context.label}: ${value.toFixed(1)}%`
          },
        },
      },
    },
    cutout: '55%',
  }

  return (
    <div className="max-w-md mx-auto">
      <Doughnut data={data} options={options} />
    </div>
  )
}

// Carbon Footprint Chart
function CarbonBreakdownChart({ carbonData }: { carbonData: any }) {
  if (!carbonData || !carbonData.phases || carbonData.phases.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No carbon footprint data available
      </div>
    )
  }

  const phaseLabels: Record<string, string> = {
    RAW_MATERIAL_ACQUISITION: 'Raw Materials',
    MANUFACTURING: 'Manufacturing',
    DISTRIBUTION: 'Distribution',
    USE_PHASE: 'Use Phase',
    END_OF_LIFE: 'End of Life',
  }

  const data = {
    labels: carbonData.phases.map((p: any) => phaseLabels[p.phase] || p.phase),
    datasets: [
      {
        label: 'kg CO2e',
        data: carbonData.phases.map((p: any) => p.kgCO2e),
        backgroundColor: '#013C7A',
        borderRadius: 4,
        barThickness: 24,
      },
    ],
  }

  const options = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => ` ${context.parsed.x.toFixed(1)} kg CO2e`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: '#E5E7EB' },
        ticks: { font: { family: 'Source Sans 3, sans-serif' } },
      },
      y: {
        grid: { display: false },
        ticks: { font: { family: 'Source Sans 3, sans-serif' } },
      },
    },
  }

  return (
    <div style={{ height: Math.max(200, carbonData.phases.length * 50) }}>
      <Bar data={data} options={options} />
    </div>
  )
}

// Lifecycle Timeline Component
function LifecycleTimeline({ events }: { events: LifecycleEvent[] }) {
  if (!events || events.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No lifecycle events recorded yet
      </div>
    )
  }

  const now = new Date()

  return (
    <>
      {/* Mobile: Vertical timeline */}
      <div className="md:hidden">
        <div className="relative pl-8">
          {/* Vertical line */}
          <div className="absolute left-[23px] top-6 bottom-6 w-0.5 bg-gray-200" />

          {events.map((event, idx) => {
            const eventDate = new Date(event.date)
            const isPast = eventDate <= now
            const isFuture = eventDate > now
            const colors = lifecycleColors[event.type] || lifecycleColors.INSPECTION
            const icon = lifecycleIcons[event.type] || lifecycleIcons.INSPECTION

            return (
              <div
                key={event.id}
                className="relative flex items-start gap-4 pb-6 last:pb-0 animate-fade-in-up"
                style={{ animationDelay: `${idx * 100}ms` }}
              >
                {/* Event node */}
                <div
                  className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 -ml-8 ${
                    isPast
                      ? 'bg-esf-navy border-esf-navy text-white'
                      : isFuture
                      ? 'bg-white border-gray-300 text-gray-400'
                      : 'bg-esf-coral border-esf-coral text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                  </svg>
                </div>

                {/* Event details */}
                <div className="flex-1 pt-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}
                    >
                      {event.type.replace(/_/g, ' ')}
                    </span>
                    <time className="text-xs text-gray-500">{formatShortDate(event.date)}</time>
                  </div>
                  <h4 className="mt-1 text-sm font-semibold text-gray-900 leading-tight">
                    {event.title}
                  </h4>
                  {event.description && (
                    <p className="mt-1 text-xs text-gray-500">{event.description}</p>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Desktop: Horizontal timeline */}
      <div className="hidden md:block overflow-x-auto pb-4">
        <div className="flex justify-center">
          <div
            className="grid gap-0"
            style={{
              gridTemplateColumns: `repeat(${events.length}, 160px)`,
            }}
          >
            {events.map((event, idx) => {
              const eventDate = new Date(event.date)
              const isPast = eventDate <= now
              const isFuture = eventDate > now
              const colors = lifecycleColors[event.type] || lifecycleColors.INSPECTION
              const icon = lifecycleIcons[event.type] || lifecycleIcons.INSPECTION

              return (
                <div
                  key={event.id}
                  className="flex flex-col items-center animate-fade-in-up relative"
                  style={{ animationDelay: `${idx * 100}ms` }}
                >
                  {/* Connector line */}
                  {idx > 0 && (
                    <div
                      className={`absolute h-0.5 ${isPast ? 'bg-esf-navy' : 'bg-gray-200'}`}
                      style={{
                        width: '100px',
                        top: '24px',
                        right: '50%',
                        marginRight: '24px',
                      }}
                    />
                  )}

                  {/* Event node */}
                  <div
                    className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all flex-shrink-0 ${
                      isPast
                        ? 'bg-esf-navy border-esf-navy text-white'
                        : isFuture
                        ? 'bg-white border-gray-300 text-gray-400'
                        : 'bg-esf-coral border-esf-coral text-white'
                    }`}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
                    </svg>
                  </div>

                  {/* Event details */}
                  <div className="mt-3 text-center w-full px-2">
                    <span
                      className={`inline-block px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${colors.bg} ${colors.text}`}
                    >
                      {event.type.replace(/_/g, ' ')}
                    </span>
                    <h4 className="mt-1 text-sm font-semibold text-gray-900 leading-tight">
                      {event.title}
                    </h4>
                    <time className="text-xs text-gray-500 block">{formatShortDate(event.date)}</time>
                    {event.description && (
                      <p className="mt-1 text-xs text-gray-500">{event.description}</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </>
  )
}

// Maintenance History List
function MaintenanceHistory({ records }: { records: PublicMaintenanceRecord[] }) {
  if (!records || records.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No maintenance records available
      </div>
    )
  }

  const typeIcons: Record<string, string> = {
    INSPECTION: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
    MAINTENANCE: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    REPAIR: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z',
  }

  return (
    <div className="space-y-3">
      {records.map((record, idx) => (
        <div
          key={record.id}
          className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-100 animate-fade-in-up"
          style={{ animationDelay: `${idx * 50}ms` }}
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-esf-navy/10 flex items-center justify-center">
            <svg className="w-5 h-5 text-esf-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={typeIcons[record.type] || typeIcons.MAINTENANCE} />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-xs font-medium rounded bg-esf-navy/10 text-esf-navy">
                {record.type}
              </span>
              <span
                className={`px-2 py-0.5 text-xs font-medium rounded ${
                  record.status === 'COMPLETED'
                    ? 'bg-green-100 text-green-700'
                    : record.status === 'IN_PROGRESS'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-blue-100 text-blue-700'
                }`}
              >
                {record.status.replace(/_/g, ' ')}
              </span>
            </div>
            <h4 className="mt-1 font-medium text-gray-900">{record.summary}</h4>
            <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
              <time>{formatDate(record.date)}</time>
              {record.outcome && <span>{record.outcome}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// Version History List
function VersionHistory({ entries }: { entries: DppVersionEntry[] }) {
  if (!entries || entries.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No version history available
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {entries.map((entry, idx) => (
        <div
          key={`${entry.version}-${entry.timestamp}`}
          className="p-4 bg-gray-50 rounded-lg border border-gray-100 animate-fade-in-up"
          style={{ animationDelay: `${idx * 50}ms` }}
        >
          <div className="flex items-center justify-between">
            <span className="px-3 py-1 bg-esf-navy text-white text-sm font-bold rounded-full">
              v{entry.version}
            </span>
            <time className="text-sm text-gray-500">{formatDate(entry.timestamp)}</time>
          </div>
          {entry.reason && (
            <p className="mt-2 text-gray-700">{entry.reason}</p>
          )}
          {entry.changes && entry.changes.length > 0 && (
            <div className="mt-3 space-y-1">
              {entry.changes.map((change, cIdx) => (
                <div key={cIdx} className="text-sm text-gray-600 flex items-center gap-2">
                  <span
                    className={`w-2 h-2 rounded-full ${
                      change.changeType === 'ADDED'
                        ? 'bg-green-500'
                        : change.changeType === 'REMOVED'
                        ? 'bg-red-500'
                        : 'bg-amber-500'
                    }`}
                  />
                  <span className="font-medium">{change.field}</span>
                  <span className="text-gray-400">
                    {change.changeType === 'ADDED'
                      ? 'added'
                      : change.changeType === 'REMOVED'
                      ? 'removed'
                      : 'modified'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// Section Navigation
const sections = [
  { id: 'overview', label: 'Overview' },
  { id: 'materials', label: 'Materials' },
  { id: 'substances', label: 'Substances' },
  { id: 'sustainability', label: 'Sustainability' },
  { id: 'supplychain', label: 'Supply Chain' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'maintenance', label: 'Maintenance' },
  { id: 'versions', label: 'History' },
  { id: 'endoflife', label: 'End of Life' },
  { id: 'documents', label: 'Documents' },
]

// Main Component
export default function PublicPassportPage({ passportId }: PublicPassportPageProps) {
  const base = useMemo(() => getApiBase(), [])
  const [product, setProduct] = useState<Product | null>(null)
  const [history, setHistory] = useState<PublicPassportHistory | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeSection, setActiveSection] = useState('overview')
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({})

  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)
      try {
        const [productRes, historyRes] = await Promise.all([
          fetch(`${base}/public/passport/${passportId}`),
          fetch(`${base}/public/passport/${passportId}/history`),
        ])

        if (!productRes.ok) {
          if (productRes.status === 404) {
            throw new Error('Product passport not found')
          }
          throw new Error('Failed to fetch passport data')
        }

        const productData = await productRes.json()
        setProduct(productData)

        if (historyRes.ok) {
          const historyData = await historyRes.json()
          setHistory(historyData)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [base, passportId])

  // Intersection observer for active section
  useEffect(() => {
    const observers: IntersectionObserver[] = []

    sections.forEach((section) => {
      const element = sectionRefs.current[section.id]
      if (!element) return

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(section.id)
            }
          })
        },
        { threshold: 0.3, rootMargin: '-100px 0px -50% 0px' }
      )

      observer.observe(element)
      observers.push(observer)
    })

    return () => observers.forEach((obs) => obs.disconnect())
  }, [product])

  // Scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId]
    if (element) {
      const headerOffset = 140
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  // Handle print
  const handlePrint = () => window.print()

  // Handle JSON-LD export
  const handleExportJsonLd = async () => {
    try {
      const res = await fetch(`${base}/public/passport/${passportId}`, {
        headers: { Accept: 'application/ld+json' },
      })
      const data = await res.json()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/ld+json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `passport-${passportId}.jsonld`
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to export JSON-LD:', err)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600&display=swap');
        `}</style>
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto">
            <div className="absolute inset-0 border-4 border-esf-navy/20 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-esf-navy border-t-transparent rounded-full animate-spin"></div>
          </div>
          <p className="mt-4 text-gray-600" style={{ fontFamily: 'Sora, sans-serif' }}>
            Loading Digital Product Passport...
          </p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600&display=swap');
        `}</style>
        <div className="text-center max-w-md">
          <div className="w-20 h-20 mx-auto bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="mt-4 text-xl font-bold text-gray-900" style={{ fontFamily: 'Sora, sans-serif' }}>
            Passport Not Found
          </h1>
          <p className="mt-2 text-gray-600" style={{ fontFamily: 'Source Sans 3, sans-serif' }}>
            {error || 'The digital product passport you are looking for could not be found.'}
          </p>
          <p className="mt-4 text-sm text-gray-500">
            Passport ID: <code className="px-2 py-1 bg-gray-100 rounded">{passportId}</code>
          </p>
        </div>
      </div>
    )
  }

  const categoryLabel = product.category ? ProductCategoryLabels[product.category] || product.category : 'Product'

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Google Fonts */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700&family=Source+Sans+3:wght@400;500;600&display=swap');

        .font-display { font-family: 'Sora', sans-serif; }
        .font-body { font-family: 'Source Sans 3', sans-serif; }

        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.5s ease-out forwards;
          opacity: 0;
        }

        @media print {
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
        }
      `}</style>

      {/* Hero Section */}
      <header className="relative bg-gradient-to-br from-esf-navy via-esf-navy to-esf-navyDark overflow-hidden">
        {/* Geometric pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-8 sm:py-12">
          {/* Top bar with logo and actions */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-white rounded-lg p-1.5">
                <img src="/logo.svg" alt="ESF" className="h-8 w-auto" />
              </div>
              <span className="text-white font-bold font-display text-lg">Digital Passport</span>
            </div>

            <div className="flex items-center gap-2 no-print">
              <button
                onClick={handlePrint}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Print Passport"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
              </button>
              <button
                onClick={handleExportJsonLd}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                title="Export JSON-LD"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </button>
            </div>
          </div>

          {/* Product info */}
          <div className="grid md:grid-cols-3 gap-8 items-start">
            <div className="md:col-span-2 animate-fade-in-up">
              <span className="inline-block px-3 py-1 bg-esf-coral text-white text-sm font-medium rounded-full mb-4">
                {categoryLabel}
              </span>
              <h1 className="text-3xl sm:text-4xl font-bold text-white font-display leading-tight">
                {product.name}
              </h1>
              {product.model && (
                <p className="mt-2 text-white/80 text-lg font-body">Model: {product.model}</p>
              )}
              {product.manufacturer && (
                <p className="mt-1 text-white/60 font-body">
                  by {product.manufacturer}
                  {product.manufacturerCountry && ` (${product.manufacturerCountry})`}
                </p>
              )}

              {/* Key identifiers */}
              <div className="mt-6 flex flex-wrap gap-4 text-sm">
                {product.gtin && (
                  <div className="bg-white/10 rounded-lg px-3 py-2">
                    <span className="text-white/60 block text-xs">GTIN</span>
                    <span className="text-white font-mono">{product.gtin}</span>
                  </div>
                )}
                {product.serialNumber && (
                  <div className="bg-white/10 rounded-lg px-3 py-2">
                    <span className="text-white/60 block text-xs">Serial</span>
                    <span className="text-white font-mono">{product.serialNumber}</span>
                  </div>
                )}
              </div>
            </div>

            {/* QR Code + Passport ID */}
            <div className="flex flex-col items-center animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <div className="bg-white rounded-2xl p-4 shadow-xl">
                <QRCodeGenerator passportId={passportId} size={140} />
              </div>
              <div className="mt-4 text-center">
                <span className="text-white/60 text-xs font-body">PASSPORT ID</span>
                <p className="text-white font-mono font-bold">{passportId}</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Quick Stats Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <ScoreGauge
              score={product.carbonFootprint || 0}
              maxScore={1000}
              label="Carbon Footprint"
              unit="kg CO2e"
              size={100}
            />
            <ScoreGauge
              score={product.recyclabilityScore || 0}
              maxScore={100}
              label="Recyclability Score"
              unit="/100"
              size={100}
            />
            <ScoreGauge
              score={product.repairabilityScore || 0}
              maxScore={10}
              label="Repairability Score"
              unit="/10"
              size={100}
            />
            <ScoreGauge
              score={product.expectedLifespan || 0}
              maxScore={50}
              label="Expected Lifespan"
              unit="years"
              size={100}
            />
          </div>
        </div>
      </div>

      {/* Sticky Navigation */}
      <nav className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm no-print">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition-colors font-body ${
                  activeSection === section.id
                    ? 'bg-esf-navy text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8 space-y-12">
        {/* Overview Section */}
        <section
          id="overview"
          ref={(el) => (sectionRefs.current['overview'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Product Overview</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6 animate-fade-in-up">
              <h3 className="font-semibold text-gray-900 mb-4 font-display">Identification</h3>
              <dl className="space-y-3 font-body">
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <dt className="text-gray-500">Passport ID</dt>
                  <dd className="font-medium text-gray-900 font-mono">{product.passportId}</dd>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-100">
                  <dt className="text-gray-500">Category</dt>
                  <dd className="font-medium text-gray-900">{categoryLabel}</dd>
                </div>
                {product.subcategory && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Subcategory</dt>
                    <dd className="font-medium text-gray-900">{product.subcategory}</dd>
                  </div>
                )}
                {product.gtin && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">GTIN</dt>
                    <dd className="font-medium text-gray-900 font-mono">{product.gtin}</dd>
                  </div>
                )}
                {product.serialNumber && (
                  <div className="flex justify-between py-2">
                    <dt className="text-gray-500">Serial Number</dt>
                    <dd className="font-medium text-gray-900 font-mono">{product.serialNumber}</dd>
                  </div>
                )}
              </dl>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
              <h3 className="font-semibold text-gray-900 mb-4 font-display">Key Dates</h3>
              <dl className="space-y-3 font-body">
                {product.installationDate && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Installation Date</dt>
                    <dd className="font-medium text-gray-900">{formatDate(product.installationDate)}</dd>
                  </div>
                )}
                {product.warrantyEndDate && (
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <dt className="text-gray-500">Warranty Until</dt>
                    <dd className="font-medium text-gray-900">{formatDate(product.warrantyEndDate)}</dd>
                  </div>
                )}
                {product.expectedLifespan && (
                  <div className="flex justify-between py-2">
                    <dt className="text-gray-500">Expected Lifespan</dt>
                    <dd className="font-medium text-gray-900">{product.expectedLifespan} years</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Certifications */}
          {product.certifications && product.certifications.length > 0 && (
            <div className="mt-6 bg-white rounded-xl border border-gray-200 p-6 animate-fade-in-up" style={{ animationDelay: '200ms' }}>
              <h3 className="font-semibold text-gray-900 mb-4 font-display">Certifications</h3>
              <div className="flex flex-wrap gap-3">
                {product.certifications.map((cert: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg"
                  >
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                    <span className="font-medium text-green-800">
                      {typeof cert === 'string' ? cert : cert.name || cert.standard}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Materials Section */}
        <section
          id="materials"
          ref={(el) => (sectionRefs.current['materials'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Material Composition</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <MaterialCompositionChart materials={product.materials || []} />

            {/* Material details */}
            {product.materials && product.materials.length > 0 && (
              <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {product.materials.map((material: any, idx: number) => (
                  <div
                    key={idx}
                    className="p-4 bg-gray-50 rounded-lg border border-gray-100 animate-fade-in-up"
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <h4 className="font-semibold text-gray-900 font-display">
                      {material.name || material.materialType}
                    </h4>
                    <div className="mt-2 space-y-1 text-sm font-body">
                      {material.percentageByWeight && (
                        <p className="text-gray-600">
                          <span className="font-medium">{material.percentageByWeight}%</span> by weight
                        </p>
                      )}
                      {material.weightGrams && (
                        <p className="text-gray-600">
                          <span className="font-medium">{material.weightGrams}g</span>
                        </p>
                      )}
                      {material.isRecycled && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                          </svg>
                          Recycled
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Substances of Concern Section */}
        <section
          id="substances"
          ref={(el: HTMLElement | null) => (sectionRefs.current['substances'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Substances of Concern</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {product.substancesOfConcern && product.substancesOfConcern.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <div className="px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
                    {product.substancesOfConcern.length} substance{product.substancesOfConcern.length !== 1 ? 's' : ''} declared
                  </div>
                </div>
                <div className="grid gap-4">
                  {product.substancesOfConcern.map((substance: any, idx: number) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h4 className="font-semibold text-gray-900">{substance.commonName || substance.chemicalName}</h4>
                          {substance.chemicalName !== substance.commonName && (
                            <p className="text-sm text-gray-500">{substance.chemicalName}</p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          substance.category === 'SVHC' ? 'bg-red-100 text-red-700' :
                          substance.category === 'CLP_HAZARDOUS' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {substance.category?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        {substance.casNumber && (
                          <div>
                            <span className="text-gray-500">CAS Number:</span>
                            <span className="ml-2 text-gray-900 font-mono">{substance.casNumber}</span>
                          </div>
                        )}
                        {substance.locationInProduct && (
                          <div>
                            <span className="text-gray-500">Location:</span>
                            <span className="ml-2 text-gray-900">{substance.locationInProduct}</span>
                          </div>
                        )}
                        {substance.concentrationPercent && (
                          <div>
                            <span className="text-gray-500">Concentration:</span>
                            <span className="ml-2 text-gray-900">{substance.concentrationPercent}{substance.concentrationUnit || '%'}</span>
                          </div>
                        )}
                      </div>
                      {substance.safeUseInstructions && (
                        <div className="mt-3 p-3 bg-blue-50 rounded text-sm text-blue-800">
                          <strong>Safe use:</strong> {substance.safeUseInstructions}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">No substances of concern declared</p>
                <p className="text-sm text-gray-500 mt-1">This product does not contain reportable hazardous substances</p>
              </div>
            )}
          </div>
        </section>

        {/* Sustainability Section */}
        <section
          id="sustainability"
          ref={(el: HTMLElement | null) => (sectionRefs.current['sustainability'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Sustainability & Carbon Footprint</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 font-display">Carbon Breakdown by Phase</h3>
              <CarbonBreakdownChart carbonData={product.carbonFootprintData} />
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4 font-display">Environmental Scores</h3>
              <div className="grid grid-cols-2 gap-6">
                <ScoreGauge
                  score={product.recyclabilityScore || 0}
                  maxScore={100}
                  label="Recyclability"
                  unit="%"
                />
                <ScoreGauge
                  score={product.repairabilityScore || 0}
                  maxScore={10}
                  label="Repairability"
                  unit="/10"
                />
              </div>

              {/* Additional sustainability info */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3 font-body">
                {product.carbonFootprint && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Total Carbon Footprint</span>
                    <span className="font-semibold text-gray-900">{product.carbonFootprint} kg CO2e</span>
                  </div>
                )}
                {product.expectedLifespan && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Design Lifespan</span>
                    <span className="font-semibold text-gray-900">{product.expectedLifespan} years</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Supply Chain Section */}
        <section
          id="supplychain"
          ref={(el: HTMLElement | null) => (sectionRefs.current['supplychain'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Supply Chain</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {product.supplyChain && product.supplyChain.nodes && product.supplyChain.nodes.length > 0 ? (
              <div className="space-y-6">
                {/* Supply chain metadata */}
                <div className="flex flex-wrap gap-3 mb-6">
                  {product.supplyChain.conflictMineralsFree && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Conflict Minerals Free
                    </span>
                  )}
                  {product.supplyChain.deforestationFree && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Deforestation Free
                    </span>
                  )}
                  {product.supplyChain.dueDiligenceCompliant && (
                    <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      Due Diligence Compliant
                    </span>
                  )}
                </div>

                {/* Supplier nodes */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900 font-display">Supplier Network</h3>
                  <div className="grid gap-4">
                    {product.supplyChain.nodes.map((node: any, idx: number) => (
                      <div
                        key={idx}
                        className="border border-gray-200 rounded-lg p-4 animate-fade-in-up"
                        style={{ animationDelay: `${idx * 50}ms` }}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <span className={`px-2 py-1 rounded text-xs font-bold ${
                              node.tier === 1 ? 'bg-esf-navy text-white' :
                              node.tier === 2 ? 'bg-blue-600 text-white' :
                              'bg-blue-400 text-white'
                            }`}>
                              Tier {node.tier}
                            </span>
                            <h4 className="font-semibold text-gray-900">{node.name}</h4>
                          </div>
                          {node.dueDiligenceStatus && (
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              node.dueDiligenceStatus === 'VERIFIED' ? 'bg-green-100 text-green-700' :
                              node.dueDiligenceStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {node.dueDiligenceStatus}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm mt-3">
                          {node.country && (
                            <div>
                              <span className="text-gray-500">Country:</span>
                              <span className="ml-2 text-gray-900">{node.country}</span>
                            </div>
                          )}
                          {node.componentSupplied && (
                            <div>
                              <span className="text-gray-500">Component:</span>
                              <span className="ml-2 text-gray-900">{node.componentSupplied}</span>
                            </div>
                          )}
                          {node.certifications && node.certifications.length > 0 && (
                            <div className="col-span-2 md:col-span-1">
                              <span className="text-gray-500">Certifications:</span>
                              <span className="ml-2 text-gray-900">{node.certifications.join(', ')}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">Supply chain information not available</p>
                <p className="text-sm text-gray-500 mt-1">Detailed supplier data has not been provided for this product</p>
              </div>
            )}
          </div>
        </section>

        {/* Timeline Section */}
        <section
          id="timeline"
          ref={(el) => (sectionRefs.current['timeline'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Product Lifecycle</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6 overflow-hidden">
            <LifecycleTimeline events={history?.lifecycleEvents || []} />
          </div>
        </section>

        {/* Maintenance Section */}
        <section
          id="maintenance"
          ref={(el) => (sectionRefs.current['maintenance'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Maintenance History</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <MaintenanceHistory records={history?.maintenance || []} />
          </div>
        </section>

        {/* Version History Section */}
        <section
          id="versions"
          ref={(el) => (sectionRefs.current['versions'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Passport Version History</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <VersionHistory entries={history?.versionHistory || []} />
          </div>
        </section>

        {/* End of Life Section */}
        <section
          id="endoflife"
          ref={(el: HTMLElement | null) => (sectionRefs.current['endoflife'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">End of Life & Recycling</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {product.endOfLifeData || product.recyclabilityScore ? (
              <div className="space-y-8">
                {/* Recycling summary */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm text-green-600">Recyclability</p>
                      <p className="font-semibold text-green-800 text-lg">
                        {product.recyclabilityScore || product.endOfLifeData?.recyclableMaterialsPercent || 0}%
                      </p>
                    </div>
                  </div>

                  {product.endOfLifeData?.disassemblyInstructions && (
                    <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-blue-600">Disassembly Steps</p>
                        <p className="font-semibold text-blue-800 text-lg">
                          {product.endOfLifeData.disassemblyInstructions.length} steps
                        </p>
                      </div>
                    </div>
                  )}

                  {product.endOfLifeData?.totalDisassemblyTimeMinutes && (
                    <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-lg">
                      <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-amber-600">Disassembly Time</p>
                        <p className="font-semibold text-amber-800 text-lg">
                          {product.endOfLifeData.totalDisassemblyTimeMinutes} min
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Disassembly instructions */}
                {product.endOfLifeData?.disassemblyInstructions && product.endOfLifeData.disassemblyInstructions.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4 font-display">Disassembly Instructions</h3>
                    <div className="space-y-3">
                      {product.endOfLifeData.disassemblyInstructions.map((step: any, idx: number) => (
                        <div
                          key={idx}
                          className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg animate-fade-in-up"
                          style={{ animationDelay: `${idx * 50}ms` }}
                        >
                          <div className="flex-shrink-0 w-8 h-8 bg-esf-navy text-white rounded-full flex items-center justify-center font-bold text-sm">
                            {step.stepNumber || idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{step.instruction}</p>
                            <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-600">
                              {step.componentRemoved && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                  Component: {step.componentRemoved}
                                </span>
                              )}
                              {step.materialType && (
                                <span className="px-2 py-1 bg-gray-200 text-gray-700 rounded">
                                  Material: {step.materialType}
                                </span>
                              )}
                              {step.recyclingStream && (
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded">
                                  {step.recyclingStream}
                                </span>
                              )}
                            </div>
                            {step.toolsRequired && step.toolsRequired.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                <span className="text-sm text-gray-500">Tools:</span>
                                {step.toolsRequired.map((tool: string, toolIdx: number) => (
                                  <span key={toolIdx} className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">
                                    {tool}
                                  </span>
                                ))}
                              </div>
                            )}
                            {step.estimatedTimeMinutes && (
                              <p className="mt-1 text-sm text-gray-500">
                                Estimated time: {step.estimatedTimeMinutes} minutes
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special tools required */}
                {product.endOfLifeData?.specialToolsRequired && product.endOfLifeData.specialToolsRequired.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-4 font-display">Special Tools Required</h3>
                    <div className="flex flex-wrap gap-3">
                      {product.endOfLifeData.specialToolsRequired.map((tool: string, idx: number) => (
                        <span key={idx} className="px-3 py-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg">
                          {tool}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recycling breakdown */}
                <div className="grid md:grid-cols-3 gap-4">
                  {product.endOfLifeData?.recyclableMaterialsPercent !== undefined && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{product.endOfLifeData.recyclableMaterialsPercent}%</p>
                      <p className="text-sm text-green-600">Recyclable Materials</p>
                    </div>
                  )}
                  {product.endOfLifeData?.recoverableEnergyPercent !== undefined && product.endOfLifeData.recoverableEnergyPercent > 0 && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{product.endOfLifeData.recoverableEnergyPercent}%</p>
                      <p className="text-sm text-blue-600">Energy Recovery</p>
                    </div>
                  )}
                  {product.endOfLifeData?.landfillPercent !== undefined && (
                    <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                      <p className="text-2xl font-bold text-gray-700">{product.endOfLifeData.landfillPercent}%</p>
                      <p className="text-sm text-gray-600">Landfill</p>
                    </div>
                  )}
                </div>

                {/* Take-back information */}
                {product.endOfLifeData?.takeBackSchemeAvailable && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <h4 className="font-semibold text-blue-800">Take-Back Programme Available</h4>
                        <div className="mt-2 space-y-1 text-sm text-blue-700">
                          {product.endOfLifeData.takeBackSchemeUrl && (
                            <p>
                              <a href={product.endOfLifeData.takeBackSchemeUrl} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                                View take-back programme details
                              </a>
                            </p>
                          )}
                          {product.endOfLifeData.takeBackSchemePhone && (
                            <p>Phone: {product.endOfLifeData.takeBackSchemePhone}</p>
                          )}
                          {product.endOfLifeData.collectionPointsUrl && (
                            <p>
                              <a href={product.endOfLifeData.collectionPointsUrl} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                                Find collection points
                              </a>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Local recycling info */}
                {product.endOfLifeData?.localRecyclingInfoUrl && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-600">
                      For local recycling information, visit:{' '}
                      <a href={product.endOfLifeData.localRecyclingInfoUrl} target="_blank" rel="noopener noreferrer" className="text-esf-navy underline hover:no-underline">
                        {product.endOfLifeData.localRecyclingInfoUrl}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-8">
                End of life information not available for this product
              </div>
            )}
          </div>
        </section>

        {/* Documents Section */}
        <section
          id="documents"
          ref={(el: HTMLElement | null) => (sectionRefs.current['documents'] = el)}
          className="scroll-mt-36"
        >
          <h2 className="text-2xl font-bold text-gray-900 font-display mb-6">Documents & Resources</h2>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {product.documents && product.documents.length > 0 ? (
              <div className="grid gap-4">
                {product.documents.map((doc: any, idx: number) => {
                  const typeIcons: Record<string, string> = {
                    DECLARATION_OF_CONFORMITY: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                    INSTALLATION_MANUAL: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
                    MAINTENANCE_GUIDE: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
                    WARRANTY_CERTIFICATE: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
                    MATERIAL_SAFETY_DATA_SHEET: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                    EPD: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                    TECHNICAL_SPEC: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
                  }
                  const iconPath = typeIcons[doc.type] || 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'

                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors animate-fade-in-up"
                      style={{ animationDelay: `${idx * 50}ms` }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-esf-navy/10 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-esf-navy" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
                          </svg>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{doc.title || doc.name}</h4>
                          <div className="flex items-center gap-3 mt-1 text-sm">
                            <span className="text-gray-500">{doc.type?.replace(/_/g, ' ')}</span>
                            {doc.language && <span className="text-gray-400">• {doc.language}</span>}
                            {doc.fileSize && <span className="text-gray-400">• {doc.fileSize}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {doc.accessTier && (
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            doc.accessTier === 'PUBLIC' ? 'bg-green-100 text-green-700' :
                            doc.accessTier === 'REGISTERED' ? 'bg-blue-100 text-blue-700' :
                            'bg-amber-100 text-amber-700'
                          }`}>
                            {doc.accessTier}
                          </span>
                        )}
                        {doc.url && (
                          <a
                            href={doc.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-esf-navy hover:bg-esf-navy/10 rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-600 font-medium">No documents available</p>
                <p className="text-sm text-gray-500 mt-1">Product documentation has not been provided</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-esf-navyDark text-white mt-16">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Branding */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-white rounded-lg p-1.5">
                  <img src="/logo.svg" alt="ESF" className="h-8 w-auto" />
                </div>
                <span className="font-bold font-display text-lg">ESF Digital Passport</span>
              </div>
              <p className="text-white/60 text-sm font-body">
                This Digital Product Passport complies with the EU Ecodesign for Sustainable Products Regulation (ESPR).
              </p>
            </div>

            {/* Compliance */}
            <div>
              <h3 className="font-semibold mb-4 font-display">Compliance</h3>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-white/10 rounded text-sm">EU ESPR</span>
                <span className="px-3 py-1 bg-white/10 rounded text-sm">GS1 Digital Link</span>
                <span className="px-3 py-1 bg-white/10 rounded text-sm">JSON-LD</span>
              </div>
            </div>

            {/* Actions */}
            <div>
              <h3 className="font-semibold mb-4 font-display">Export Data</h3>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleExportJsonLd}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm no-print"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download JSON-LD
                </button>
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-sm no-print"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  Print Passport
                </button>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center text-white/40 text-sm font-body">
            <p>Passport ID: {passportId}</p>
            <p className="mt-1">Generated by ESF Digital Product Passport Platform</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
